import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  HeartHandshake, Sparkles, Search, Calendar, MapPin, ExternalLink,
  Loader2, Receipt,
} from "lucide-react";

/**
 * DonorDashboard
 * --------------
 * Landing page for accounts that signed up as donors. Shows:
 *
 *   • Three rollup cards — Lifetime given, Number of donations, Nonprofits
 *     supported. Only PAID donations count in the totals (pending Stripe
 *     Checkout sessions are still shown but flagged).
 *   • Per-donation card list with amount, nonprofit, event, receipt link
 *     when available, and a "Pending" badge if the Stripe session never
 *     completed.
 *   • Empty state that punches through to /give-back for a fresh donor.
 *
 * Retroactive: donations made under this email BEFORE the account was
 * created also show up, since donations are indexed by donor_email
 * server-side.
 */
function centsToDollars(cents) {
  return (Number(cents || 0) / 100).toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
}

export default function DonorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/donor/my-donations");
        setData(d);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your donation history");
      } finally { setLoading(false); }
    })();
  }, []);

  const totals = data?.totals || { lifetime_cents: 0, donations_count: 0, nonprofits_supported: 0 };
  const items = data?.items || [];

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-10" data-testid="donor-dashboard">
      {/* Hero */}
      <div className="flex items-center gap-3">
        <span className="rounded-full p-3"
              style={{ backgroundColor: "rgba(236,72,153,0.15)", color: "var(--avt-pink)" }}>
          <HeartHandshake className="h-6 w-6" />
        </span>
        <div>
          <div className="overline" style={{ color: "var(--avt-pink)" }}>Donor</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {user?.name ? <>Hi {user.name.split(" ")[0]},</> : <>Welcome,</>}{" "}
            <span style={{ color: "var(--avt-pink)" }}>your generosity matters.</span>
          </h1>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Every donation you make lives here — with receipts, the nonprofits, and the events they funded.
      </p>

      {/* Rollup cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="donor-totals">
        <div className="rounded-xl p-4 sm:p-5 border-2"
             style={{ backgroundColor: "rgba(236,72,153,0.06)", borderColor: "rgba(236,72,153,0.35)" }}>
          <div className="overline" style={{ color: "var(--avt-pink)" }}>Lifetime given</div>
          <div className="font-serif text-4xl mt-1" data-testid="totals-lifetime">
            {centsToDollars(totals.lifetime_cents)}
          </div>
        </div>
        <div className="rounded-xl p-4 sm:p-5 border-2"
             style={{ backgroundColor: "rgba(107,47,186,0.06)", borderColor: "rgba(107,47,186,0.35)" }}>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>Donations</div>
          <div className="font-serif text-4xl mt-1" data-testid="totals-count">
            {totals.donations_count}
          </div>
        </div>
        <div className="rounded-xl p-4 sm:p-5 border-2"
             style={{ backgroundColor: "rgba(249,115,22,0.06)", borderColor: "rgba(249,115,22,0.35)" }}>
          <div className="overline" style={{ color: "var(--avt-orange)" }}>Nonprofits supported</div>
          <div className="font-serif text-4xl mt-1" data-testid="totals-nonprofits">
            {totals.nonprofits_supported}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-4">
        <Button
          onClick={() => navigate("/give-back")}
          data-testid="find-causes-btn"
          variant="outline"
          className="rounded-full px-5 py-2.5 inline-flex items-center gap-2"
        >
          <Search className="h-4 w-4" /> Find causes to support
        </Button>
      </div>

      {/* Donation list */}
      <h2 className="font-serif text-2xl mt-10 mb-3">Your giving history</h2>
      {loading ? (
        <div className="py-12 text-center" data-testid="donor-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed p-8 text-center" data-testid="donor-empty"
             style={{ borderColor: "rgba(236,72,153,0.25)" }}>
          <Sparkles className="h-8 w-8 mx-auto" style={{ color: "var(--avt-pink)" }} />
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
            You haven&rsquo;t made any donations yet. Browse the Give Back directory to support a cause you care about.
          </p>
          <Link to="/give-back" data-testid="donor-browse-link"
                className="btn-primary mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full">
            <Search className="h-4 w-4" /> Browse Give Back
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="donor-list">
          {items.map((d) => (
            <li key={d.id}
                data-testid={`donation-row-${d.id}`}
                className="rounded-xl border p-4 sm:p-5 card-warm">
              <div className="flex items-start gap-4">
                <div className="text-right">
                  <div className="font-serif text-2xl"
                       data-testid={`donation-amount-${d.id}`}
                       style={{ color: d.status === "paid" ? "var(--avt-pink)" : "var(--ink-soft)" }}>
                    {centsToDollars(d.amount_cents)}
                  </div>
                  {d.tip_cents > 0 && (
                    <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                      + {centsToDollars(d.tip_cents)} tip
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {d.nonprofit ? (
                    <Link
                      to={d.nonprofit.slug ? `/@${d.nonprofit.slug}` : "/nonprofits"}
                      data-testid={`donation-nonprofit-${d.id}`}
                      className="font-serif text-lg hover:underline block truncate"
                    >
                      {d.nonprofit.name}
                    </Link>
                  ) : (
                    <div className="font-serif text-lg">Nonprofit no longer listed</div>
                  )}
                  {d.event && (
                    <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1"
                         style={{ color: "var(--ink-soft)" }}>
                      <Link to={`/events/${d.event.id}`}
                            data-testid={`donation-event-${d.id}`}
                            className="inline-flex items-center gap-1 hover:underline">
                        <Calendar className="h-3 w-3" /> {d.event.title}
                      </Link>
                      {d.event.city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {d.event.city}
                        </span>
                      )}
                    </div>
                  )}
                  {d.message && (
                    <div className="mt-2 text-xs italic" style={{ color: "var(--ink-soft)" }}>
                      &ldquo;{d.message}&rdquo;
                    </div>
                  )}
                  <div className="mt-2 text-[11px]" style={{ color: "var(--ink-soft)" }}>
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {d.status === "paid" ? (
                    <span
                      data-testid={`donation-status-${d.id}`}
                      className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1 rounded-full px-2 py-1"
                      style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A" }}
                    >
                      Paid
                    </span>
                  ) : (
                    <span
                      data-testid={`donation-status-${d.id}`}
                      className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1 rounded-full px-2 py-1"
                      style={{ backgroundColor: "rgba(249,115,22,0.14)", color: "var(--avt-orange)" }}
                    >
                      Pending
                    </span>
                  )}
                  {d.receipt_url && (
                    <a
                      href={d.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`donation-receipt-${d.id}`}
                      className="text-xs inline-flex items-center gap-1 hover:underline"
                      style={{ color: "var(--avt-teal)" }}
                    >
                      <Receipt className="h-3 w-3" /> Receipt <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
